import { useMediaQueryGs } from 'app/hooks/useMediaQuery';
import {
  Card,
  Colors,
  Form,
  SizesButton,
  StringInput,
  Text,
  Typography,
} from 'goalsetter-webapp-components';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { INPUT_NAMES } from './inputNames';
import { validationHandler } from './validationHandler';
import { isEmpty } from 'lodash';
import { t } from 'i18next';
import SimpleModal from 'app/components/Modals/SimpleModal';
import { useDisclosure } from '@mantine/hooks';
import {
  Container,
  bottomTextStyle,
  cardStyle,
  lastInputStyle,
  linkStyle,
  titleWrapperStyle,
} from './styles';
import { maskPhoneNumberOrEmail, maskedPhoneToNumber } from 'utils/string';
import { useNavigate } from 'react-router-dom';

import { RoutesGs } from '../../../../router/RoutesList';
import { sendAnalyticsEvent } from '../../../../utils/analytics/analyticsWrapper';
import { ActionType, CategoryType } from '../../../../types/analytics/enum';
import PortalTitle from 'app/components/PortalTitle';
import { goalService } from 'api';
import useFetchData from 'app/hooks/useFetchData';
import { GoalCardInvitation, GsErrorCode } from 'types/api';
import LogoHeader from 'app/components/LogoHeader';
import { Flex } from '@mantine/core';
import { POLICIES_PRIVACY, POLICIES_TERMS } from 'utils';

const SendInvitation = () => {
  const { isMobile } = useMediaQueryGs();
  const [yourName, setYourName] = useState<string>('');
  const [theirName, setTheirName] = useState<string>('');
  const [contact, setContact] = useState<string>('');

  const [isModalIsError, { open: openModalIsError, close: closeModalIsError }] =
    useDisclosure(false);

  const [isModalVisible, { close: closeModal }] = useDisclosure(false);
  const areEmpty = ![yourName, theirName, contact].every((str) => {
    return !isEmpty(str);
  });

  const navigate = useNavigate();

  const {
    data,
    loading,
    fetchData: sendInvitation,
    error,
  } = useFetchData<
    any,
    { 'status-code': number; 'status-description': string }
  >();

  const {
    setError,
    clearErrors,
    register,
    formState: { errors },
  } = useForm();

  const validateYourName = () =>
    validationHandler(setError, clearErrors, yourName, INPUT_NAMES.YOUR_NAME);

  const validateTheirName = () =>
    validationHandler(setError, clearErrors, theirName, INPUT_NAMES.THEIR_NAME);

  const validateContact = () =>
    validationHandler(setError, clearErrors, contact, INPUT_NAMES.CONTACT);

  const getRecaptchaToken = async () => {
    const recaptchaToken = await window?.grecaptcha?.enterprise?.execute(
      process.env.REACT_APP_SITE_KEY || 'nokey',
      {
        action: 'goalcardinvite',
      },
    );

    return recaptchaToken;
  };

  const handleSubmit = async () => {
    validationHandler(setError, clearErrors, yourName, INPUT_NAMES.YOUR_NAME);
    validationHandler(setError, clearErrors, theirName, INPUT_NAMES.THEIR_NAME);
    validationHandler(setError, clearErrors, contact, INPUT_NAMES.CONTACT);

    if (!isEmpty(errors) || loading) return;

    sendAnalyticsEvent(
      CategoryType.sendInvitationButton,
      ActionType.Goalcard_SendInvitation_SendInvitationButton,
    );

    if (isMobile && navigator.share) {
      navigator
        .share({
          title: String(t('sendInvitation.share.title')),
          text: String(t('sendInvitation.share.text')),
          url: String(process.env.REACT_APP_DOWNLOAD_APP_LINK),
        })
        .then(() =>
          navigate(RoutesGs.SuccessInvitation, { state: { name: theirName } }),
        );
      return;
    }

    try {
      const recaptchaToken = await getRecaptchaToken();

      let dataToInvite: GoalCardInvitation = {
        senderName: yourName,
        captchaToken: recaptchaToken,
        receiverName: theirName,
      };

      if (contact.includes('@')) {
        dataToInvite = { ...dataToInvite, receiverEmail: contact };
      } else {
        dataToInvite = {
          ...dataToInvite,
          receiverPhoneNumber: maskedPhoneToNumber(contact),
        };
      }

      sendInvitation(goalService.sendGoalCardInvite(dataToInvite));
    } catch (error) {
      openModalIsError();
    }
  };

  useEffect(() => {
    if (error) {
      openModalIsError();
    }
    if (data && data === 'OK') {
      return navigate(RoutesGs.SuccessInvitation, {
        state: { name: theirName.trim() },
      });
    }
    return () => {};
  }, [data, error, navigate, openModalIsError, theirName]);

  useEffect(() => {
    if (areEmpty) {
      setError('form', { message: 'Empty' });
      return;
    }
    if (!areEmpty) {
      clearErrors('form');
      return;
    }

    if (isEmpty(errors)) {
      clearErrors();
      return;
    }
  }, [yourName, theirName, contact, areEmpty, clearErrors, setError, errors]);

  useEffect(() => {
    clearErrors(INPUT_NAMES.YOUR_NAME);
  }, [yourName, clearErrors]);
  useEffect(() => {
    clearErrors(INPUT_NAMES.THEIR_NAME);
  }, [theirName, clearErrors]);
  useEffect(() => {
    clearErrors(INPUT_NAMES.CONTACT);
  }, [contact, clearErrors]);

  const onChangeContactInput = (e: any) => {
    setContact(maskPhoneNumberOrEmail(e));
  };

  const getDescription = () => {
    if (error) {
      if (
        error['status-code'] === GsErrorCode.ALREADY_INVITED ||
        error['status-code'] === GsErrorCode.ALREADY_INVITED_TIME ||
        error['status-code'] === GsErrorCode.ALREADY_INVITED_TRY
      ) {
        return String(t('sendInvitation.errorAlready'));
      }
      return String(t('sendInvitation.errorTryAgain'));
    }
  };

  return (
    <Container>
      <SimpleModal
        show={isModalVisible}
        description={String(t('sendInvitation.error'))}
        onClose={closeModal}
      />
      <LogoHeader />
      <Flex style={titleWrapperStyle}>
        <PortalTitle translation="sendInvitation.title" />
      </Flex>
      <Card style={cardStyle}>
        <Form
          onSubmit={handleSubmit}
          errors={errors}
          isLoading={loading}
          columns={isMobile ? 1 : 2}
          buttonLabel={String(t('sendInvitation.sendInvitation'))}
          buttonSize={isMobile ? SizesButton.LARGE : SizesButton.MEDIUM}
        >
          <StringInput
            label={t('sendInvitation.formInputs.yourName.label')}
            placeholder={t('sendInvitation.formInputs.yourName.placeholder')}
            onBlur={validateYourName}
            onChangeValue={setYourName}
            value={yourName}
            error={
              errors[INPUT_NAMES.YOUR_NAME]?.message
                ? String(errors[INPUT_NAMES.YOUR_NAME]?.message)
                : undefined
            }
            register={register}
            name={INPUT_NAMES.YOUR_NAME}
          />
          <StringInput
            label={t('sendInvitation.formInputs.theirName.label')}
            placeholder={t('sendInvitation.formInputs.theirName.placeholder')}
            onBlur={validateTheirName}
            onChangeValue={setTheirName}
            value={theirName}
            error={
              errors[INPUT_NAMES.THEIR_NAME]?.message
                ? String(errors[INPUT_NAMES.THEIR_NAME]?.message)
                : undefined
            }
            register={register}
            name={INPUT_NAMES.THEIR_NAME}
          />
          <StringInput
            label={t('sendInvitation.formInputs.contact.label')}
            placeholder={t('sendInvitation.formInputs.contact.placeholder')}
            onBlur={validateContact}
            onChangeValue={onChangeContactInput}
            value={contact}
            error={
              errors[INPUT_NAMES.CONTACT]?.message
                ? String(errors[INPUT_NAMES.CONTACT]?.message)
                : undefined
            }
            register={register}
            name={INPUT_NAMES.CONTACT}
            style={lastInputStyle(isMobile)}
          />
        </Form>
        <Flex style={bottomTextStyle}>
          <Text
            type={Typography.GSBODY2}
            color={Colors.MEDIUM_EMPHASIS}
          >
            {String(t('sendInvitation.googleSupport'))}
            <a
              href={POLICIES_PRIVACY}
              target="_blank"
              rel="noreferrer"
              style={linkStyle}
            >
              {String(t('sendInvitation.privacyPolicy'))}
            </a>{' '}
            {String(t('sendInvitation.and'))}
            <a
              href={POLICIES_TERMS}
              target="_blank"
              rel="noreferrer"
              style={linkStyle}
            >
              {String(t('sendInvitation.termsService'))}
            </a>
          </Text>
        </Flex>
      </Card>
      <SimpleModal
        show={isModalIsError}
        description={getDescription() || ''}
        onClose={closeModalIsError}
      ></SimpleModal>
    </Container>
  );
};

export default SendInvitation;
